export interface Params {
  enabled?: boolean;
  pageSize?: number;
  page?: number;
  searchTerm?: string;
  /** When true, `/User/All` applies city/sector/turnover/country filters server-side (discoverable members). */
  cities?: string[];
  sectors?: string[];
  turnovers?: string[];
  countries?: string[];
  /** When set, `/User/All` keeps users whose membershipName is in this list. */
  membershipNames?: string[];
  /** When set, `/User/All` keeps users with this Stripe status (case-insensitive). */
  stripeStatus?: string;
  /** @deprecated Prefer stripeStatus="active". */
  activeOnly?: boolean;
  userType?: "all" | "members" | "explorer";
  discoverableOnly?: boolean;
}

export interface PaginatedResponse<T> {
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  data: T[];
}

export interface Event {
  id: number;
  eventCode?: string | null;
  name: string;
  description: string;
  price: number;
  earlyBirdPrice: number;
  earlyBirdDateEnd: string;
  seats: number;
  location: string;
  /** When true, online event — use meetingLink; hide city on listings. */
  isOnline?: boolean;
  /** Join URL for online events (shown after booking / in email). */
  meetingLink?: string | null;
  productId: string;
  /** ISO 4217 lowercase (e.g. gbp, usd); should match Stripe Price for productId. */
  currency?: string | null;
  eventDate: string;
  /** Per-event booking email subject; merge tokens: {FirstName}, {AttendeeName}, {EventName}, {EventDate}, {EventLocation}, {EventCode} */
  bookingConfirmationEmailSubject?: string | null;
  /** HTML fragment (inner content) for booking confirmation; same tokens; same Connect email shell. */
  bookingConfirmationEmailBodyHtml?: string | null;
  /** Comma-separated membership IDs; null/empty = visible to all membership types. */
  allowedMembershipIds?: string | null;
  /** When false, Business/Executive member tier pricing does not apply (delegation/tour). */
  memberEventDiscountEligible?: boolean;
  /** Comma-separated: Explorer,EarlyStage,Business,Executive — those groups pay £0. */
  freeForGroups?: string | null;
  /** SAS URL for the event card banner; falls back to static images when unset. */
  bannerImage?: string | null;
}

export interface MuslimInvestTerms {
  id:number;
  isAccepted: number;
  dateAccepted: string;
  userId: number;
}

export interface GetEventDiscount {
  userId: number;
  eventId: number;
  amountToCharge: number;
  discountApplied: boolean;
  membership: string;
}

export interface SavePaymentMethod {
  id: number;
  userId: string;
  customerId: string;
  paymentMethodId: string;
  billingAddress: string;
  brand: string;
  last4: string;
}

export interface BookedEvents {
  consentToShareProfile: number;
  description: string;
  earlyBirdDateEnd: string;
  earlyBirdPrice: number;
  eventObjectives: string;
  id: number;
  name: string;
  price: number;
  seats: number;
  sectorsOfInterest: string;
  stageOfBusiness: string;
  location: string
  eventDate:string;
  currency?: string | null;
  isOnline?: boolean;
  meetingLink?: string | null;
  /** When false / admin-free for Executive, booking does not consume monthly complimentary. */
  memberEventDiscountEligible?: boolean;
  freeForGroups?: string | null;
}

export interface EmailSend {
  email: string;
  name: string;
  subject: string;
  body: string;
  /** Plain-text part when body is HTML */
  textBody?: string;
}

export interface EventBookingConfirmationEmail {
  /** When set, API uses this event’s stored subject/body templates (see Event). */
  eventId?: number;
  email: string;
  name: string;
  firstName?: string;
  eventName: string;
  eventDateText: string;
}

export interface SendMessages {
  id?: number;
  senderUserId: string;
  userIdMessageTo: number;
  body: string;
  dateSent: string;
}

export interface RemoveBooking {
  userId: string;
  eventId: number;
}

export interface Message {
  id: number;
  userIdMessageFrom: number;
  userIdMessageTo: number;
  body: string;
  dateSent: string;
}

export interface GetMessages {
  firstName: string;
  lastName: string;
  lastMessageDate: string;
  messages: Message[];
}

export interface ImageUrls {
  IdDocument: string;
  PersonalProfessionalPhoto: string;
  BusinessLogo: string;
  ProfessionalPhoto: string;
  BannerImage?: string;
}

export interface Notification extends ImageUrls {
  id: number;
  name: string;
  description: string;
  date: string;
  isRead: boolean;
  isUnread: boolean;
  isDeleted: boolean;
  userId: number;
}

export interface User {
  id: number;
  isAdmin?: number;
  objectId: string;
  firstName: string;
  lastName: string;
  email: string;
  isGDPRConsent: number;
  isEmailUpdates: number;
  isAnnualMembership: number;
  membershipId: number;
  membershipName: string;
  earlyStageVerificationStatus?: string;
  earlyStageAgeTier?: string;
  earlyStagePriceTier?: string;
  earlyStageVerificationRejectedReason?: string;
  isDirectory: number;
  isTermConditions: number;
  hasPaid: number;
  dateOfBirth: string;
  idDocument: string;
  phoneNumber: string;
  city: string;
  country: string;
  gender: string;
  languagePreference: string;
  professionalPhoto: string;
  aboutYou: string;
  educationLevel: string;
  studyInterest: string;
  skillsExperience: string;
  mentorshipObjectives: string;
  mentorshipExpertise: string;
  mentorshipFormat: string;
  platformInterest: string;
  peerNetworking: string;
  productInterested: string;
  businessName: string;
  businessLogo: string;
  businessSectorIndustry: string;
  businessWebsiteUrl: string;
  businessCountryOperation: string;
  businessAbout: string;
  businessAddressLineOne: string;
  businessAddressLineTwo: string;
  businessCountry: string;
  businessPostCode: string;
  businessTelephoneNumber: string;
  businessCurrency: string;
  businessTurnover: string;
  businessObjectives: string;
  businessProductServices: string;
  businessDiscount: string;
  businessDiscountCode: string;
  personalGender: string;
  personalLanguagePreference: string;
  personalAbout: string;
  personalProfessionalPhoto: string;
  personalPositionCompany: string;
  hasCompletedProfile: number;
  county: string;
  IdDocument?: string;
  PersonalProfessionalPhoto?: string;
  BusinessLogo?: string;
  ProfessionalPhoto?: string;
  DiscountCode?: string;
  DiscountEndDate?: string;
  bannerImage?: string;
  BannerImage?: string;
  universityCollege?:string;
  referralCode: string;
  referralCodeUsed?: string;
  lastLoggedIn?: string;
  customerId?: string;
  subscriptionId?: string;
  stripeStatus?: string;
  userCategory?: string;
  /** From AuthUsers.IsEmailVerified (admin user list). */
  isEmailVerified?: boolean;
  /** Explorer segmentation step (maps to Youth/Business/Executive in connections UI only). */
  explorerAttendeeType?: string;
  objectivesForAttending?: string;
  stageOfBusiness?: string;
  sectorsOfInterest?: string;
  /** Unpaid-member discovery answers + weekly limit logs (JSON string from API). */
  explorerLimitsJson?: string | null;
  /** Event-booking objectives (present on event attendee rows). */
  eventObjectives?: string;
  portalUserId?: number;
  isPortalUser?: boolean;
}

export interface Users {
  user: User;
  imageUrls: ImageUrls;
}

export interface Post {
  id: number; 
  userId: string;
  title: string;
  content: string;
  media: string;
  createdDate: string;
  isArticle: number;
  articleContent: string;
}

export interface Partner {
  id: number; 
  logo: string;
  name: string;
  description: string;
  websiteUrl: string;
  backgroundColour: string;
  fontColour: string;
  isEnabled?: number;
  Logo?: string;
}

export interface UserImageUrls {
  userId: number;
  imageUrls: ImageUrls;
}

export interface UserConnectionResponse {
  results: any;
  userDtos: User[];
  userImageUrls: UserImageUrls[];
}

export interface PaginatedUsers {
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  data: User[];
}

export interface FindUsersResponse {
  users: PaginatedUsers;
  userImageUrls: UserImageUrls[];
}

export interface PaginatedPosts {
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  data: Post[];
}

export interface PostImageUrls  {
  postId: number;
  imageUrls: {Media: string};
}

export interface FindPostsResponse {
  posts: PaginatedPosts;
  postImageUrls: PostImageUrls[];
}

export interface PartnerImageUrls  {
  partnerId: number;
  imageUrls: {Logo: string};
}

export interface PaginatedPartners {
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  data: Partner[]; 
}

export interface GetPartnersResponse {
  partnerImageUrls: PartnerImageUrls[];
  partners: PaginatedPartners;
}

export interface ConnectionRequest {
  receiverId: number;
  userId: string;
}

export interface RespondConnection {
  requestId: number;
  accept: boolean;
  senderId: number;
}

export interface Memberships {
  id: number;
  name: string;
  monthlyFee: string;
  annualFee: string;
  productId: string;
  productIdAnnual: string;
  welcomeEmailSubject?: string | null;
  welcomeEmailBodyHtml?: string | null;
}

export interface UpdateWelcomeEmailPayload {
  welcomeEmailSubject?: string | null;
  welcomeEmailBodyHtml?: string | null;
}

export interface BookEvent {
  userId: string;
  eventId: number;
  stageOfBusiness?: string;
  eventObjectives?: string;
  sectorsOfInterest?: string;
  consentToShareProfile?: number;
  /** Stripe promotion / coupon code used for this booking (if any). */
  appliedEventDiscountCode?: string;
}

interface EventUser {
  firstName: string;
  lastName: string;
  email: string;
}

export interface EventUsers {
  eventId: number;
  users: EventUser[];
}

export interface PaymentErrorLog {
  id: number;
  userId: string;
  userEmail?: string;
  userName?: string;
  productId?: string;
  paymentMethodId?: string;
  customerId?: string;
  errorType?: string;
  errorMessage: string;
  stripeErrorCode?: string;
  stripeErrorType?: string;
  requestDetails?: string;
  isEvent: boolean;
  isAnnual: boolean;
  discountCode?: string;
  errorOccurredAt: string;
}

/** Cancellation reason option (dropdown 1–7). Option 7 "Other" is free text only. */
export const CANCELLATION_REASONS = [
  {
    id: 1,
    label: "Cost no longer works for me",
    detail: "Too expensive / not enough value / budget changes",
  },
  {
    id: 2,
    label: "I don't have time to engage right now",
    detail: "Too busy / Priorities changed / Not attending events",
  },
  {
    id: 3,
    label: "It's not the right fit for my business",
    detail: "Industry doesn't match / Stage of business / Audience / Expectations",
  },
  {
    id: 4,
    label: "I didn't get the value or connections I expected",
    detail: "Event quality / Connections / Outcomes / Support",
  },
  {
    id: 5,
    label: "My business circumstances have changed",
    detail:
      "Role change / Business closed / Relocated / Company no longer supports it",
  },
  {
    id: 6,
    label: "I'm switching to another networking option",
    detail: "Another group / Another platform / Internal networking",
  },
  {
    id: 7,
    label: "Other",
    detail: "",
  },
] as const;

export interface CancellationRequestSubmit {
  objectId: string;
  reasonId: number;
  improvementFeedback?: string;
}

export interface CancellationRequest {
  id: number;
  objectId?: string;
  userId?: number;
  reasonId?: number;
  reasonCode: number;
  reasonLabel?: string;
  improvementFeedback?: string;
  status: string;
  createdAt: string;
  fullName?: string;
  businessName?: string;
  city?: string;
  country?: string;
  email?: string;
  /** User's current membership name at time of cancellation (from admin detail API). */
  membershipName?: string;
  /** Stripe customer id associated with the user (from admin detail API). */
  stripeCustomerId?: string;
  /** Stripe subscription id associated with the user (from admin detail API). */
  stripeSubscriptionId?: string;
  /** Stripe subscription status at the time of viewing (from admin detail API). */
  stripeStatus?: string;
  /** Additional user profile details for admin view. */
  phoneNumber?: string;
  businessAddressLineOne?: string;
  businessAddressLineTwo?: string;
  businessPostCode?: string;
  businessTelephoneNumber?: string;
  [key: string]: unknown;
}

export interface MeetingScheduler {
  id: number;
  membershipCategory: string;
  googleAccountEmail: string;
  firstMeetingDateTime?: string | null;
  nextSchedulerDateTime?: string | null;
  recurrenceFrequency: string;
  status: string;
  googleMeetUrl?: string | null;
  googleCalendarEventId?: string | null;
  googleCalendarId?: string | null;
  emailSubject?: string | null;
  emailBodyHtml?: string | null;
  /** Comma-separated extra invitees stored on this scheduler until an admin removes them. */
  externalAttendeeEmails?: string | null;
  /** Comma-separated emails excluded from the next invite run. */
  excludedAttendeeEmails?: string | null;
  /** When true, matching Explorers get one forum invite. Default true. */
  includeExplorers?: boolean;
  /** Also invite paid Early Stage / Youth members (ignored on Early Stage card). */
  includeEarlyStageMembers?: boolean;
  /** Also invite paid Business members (ignored on Business card). */
  includeBusinessMembers?: boolean;
  /** Also invite paid Executive members (ignored on Executive card). */
  includeExecutiveMembers?: boolean;
}

export interface MeetingSchedulerNextInvitee {
  email: string;
  fullName: string;
  /** Early Stage | Business | Executive | Member | Explorer | External | Organizer */
  kind: string;
}

export interface MeetingSchedulerCurrentGuest {
  email: string;
  displayName?: string | null;
  responseStatus?: string | null;
}

export interface MeetingSchedulerCurrentMeetingGuests {
  meetUrl?: string | null;
  canLoadGuests: boolean;
  unavailableReason?: string | null;
  guests: MeetingSchedulerCurrentGuest[];
}

export interface MeetingSchedulerGuests {
  nextInvitees: MeetingSchedulerNextInvitee[];
  excludedInvitees?: MeetingSchedulerNextInvitee[];
  currentMeeting?: MeetingSchedulerCurrentMeetingGuests | null;
}

export interface UpdateMeetingSchedulerPayload {
  googleAccountEmail: string;
  firstMeetingDateTime?: string | null;
  nextSchedulerDateTime?: string | null;
  recurrenceFrequency: string;
  emailSubject?: string | null;
  emailBodyHtml?: string | null;
  externalAttendeeEmails?: string | null;
  excludedAttendeeEmails?: string | null;
  includeExplorers?: boolean;
  includeEarlyStageMembers?: boolean;
  includeBusinessMembers?: boolean;
  includeExecutiveMembers?: boolean;
}

/** Private note a user keeps about an event attendee. */
export interface AttendeeNote {
  id: number;
  eventId: number;
  eventName: string;
  attendeeUserId: number;
  attendeeObjectId: string;
  attendeeFirstName: string;
  attendeeLastName: string;
  attendeePhoto?: string | null;
  noteText: string;
  createdAtUtc: string;
  updatedAtUtc?: string | null;
}

export interface CreateAttendeeNotePayload {
  /** Event id, or 0 for a personal note not tied to an event. */
  eventId: number;
  attendeeObjectId: string;
  noteText: string;
}

export interface UpdateAttendeeNotePayload {
  id: number;
  noteText: string;
}
